import React, { useEffect } from "react";
import {
  NavBar, StatusBadge, Button, HeroShowcase, StatBar, SectionHeader, Reveal,
  MediaPlaceholder, BentoGrid, ProjectCard, StoreBadge, BrowserFrame, PhoneFrame,
  MockScreen, Timeline, StackGrid, PrincipleCard, ContactPanel, Footer,
} from "../components/rivplay";
import { LINKS, T, EXPERIENCE, STACK } from "../content/home";
import { IAC_SERVICES } from "../content/caseStudyIAClinical";
import { useProjects, hostOf } from "../lib/firebaseProjects";
import { ServiceTiles } from "../components/ServiceTiles";

function Hero({ t, data }) {
  return (
    <section className="hp-hero" id="top" data-parallax-host>
      <div className="hp-hero-bg rp-aurora" aria-hidden="true" />
      <div className="hp-hero-bg rp-grid-bg rp-grid-fade hp-hero-grid" aria-hidden="true" />
      <div className="rp-container hp-hero-in">
        <div className="hp-hero-copy">
          <Reveal><StatusBadge>{t.status}</StatusBadge></Reveal>
          <Reveal delay={60}>
            <h1 className="rp-display-xl hp-hero-title">
              {t.heroA} <span className="hp-hero-em">{t.heroEm}</span>{t.heroB}
            </h1>
          </Reveal>
          <Reveal delay={120}><p className="rp-lead">{t.heroSub}</p></Reveal>
          <Reveal delay={180} className="hp-hero-ctas">
            <Button variant="primary" href="#proyectos" iconRight="arrow-right">{t.ctaProjects}</Button>
            <Button variant="secondary" href="#contacto" icon="mail">{t.ctaContact}</Button>
            <span className="hp-hero-social">
              <Button variant="ghost" icon="code" href={LINKS.github} aria-label="GitHub" title="GitHub" />
              <Button variant="ghost" icon="profile" href={LINKS.linkedin} aria-label="LinkedIn" title="LinkedIn" />
            </span>
          </Reveal>
          <Reveal delay={240}>
            <p className="hp-hero-meta">
              {t.heroMeta.map((m, i) => (
                <span key={m}>{i > 0 && <i aria-hidden="true">/</i>}{m}</span>
              ))}
            </p>
          </Reveal>
        </div>
        <Reveal delay={120} className={"hp-hero-visual" + (data.roperito.windowCapture ? " is-window-capture" : "")}>
          <HeroShowcase
            web={{ url: hostOf(data.roperito.site) || "roperitoweb.netlify.app", variant: "marketplace", src: data.roperito.cover }}
            mobile={{ variant: "tennis", src: data.tenapp.images?.[0] }}
            game={{ variant: "game", title: "Roam Survival", src: data.roam.cover }}
          />
        </Reveal>
      </div>
    </section>
  );
}

function About({ t }) {
  return (
    <section className="rp-section" id="sobre-mi">
      <div className="rp-container hp-about">
        <Reveal className="hp-about-photo">
          <img className="hp-about-img" src="/bryan-riveros.jpg" alt={t.photo} width="637" height="637" />
        </Reveal>
        <div className="hp-about-copy">
          <Reveal><SectionHeader index="01" eyebrow={t.aboutEyebrow} title={t.aboutTitle} /></Reveal>
          {t.about.map((p, i) => (
            <Reveal key={i} delay={60 * i}><p className="hp-about-p">{p}</p></Reveal>
          ))}
          <Reveal delay={120}>
            <dl className="hp-facts">
              {t.aboutFacts.map(([k, v]) => (
                <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// Enlaces de un proyecto: solo los que existen en Firestore.
const linksOf = (L, p, { code = true } = {}) => [
  p.site && { label: L.demo, href: p.site },
  ...(p.stores || []).map((s) => ({ label: storeName(s), href: s, icon: "play" })),
  code && p.github && { label: L.code, href: p.github, icon: "code" },
].filter(Boolean);
const storeName = (url) => (url.includes("steampowered") ? "Steam" : "Google Play");

function Projects({ t, data }) {
  const P = t.projects, L = t.l;
  const { roperito, tenapp, checkauto, roam, bullrunfrenzy, mammamia } = data;
  const phone = (variant, alt, src) => (
    <div className="rp-proj-phone">
      <PhoneFrame><MockScreen variant={variant} alt={alt} src={src} tag={null} /></PhoneFrame>
    </div>
  );
  return (
    <section className="rp-section hp-projects" id="proyectos">
      <div className="hp-projects-bg rp-aurora" aria-hidden="true" />
      <div className="rp-container" style={{ position: "relative" }}>
        <Reveal><SectionHeader index="02" eyebrow={t.projEyebrow} title={t.projTitle} lead={t.projLead} /></Reveal>
        <BentoGrid>
          <Reveal className="rp-bento-cell rp-bento-lg">
            <ProjectCard
              size="lg"
              {...P.iac}
              media={
                <ServiceTiles items={IAC_SERVICES} className="hp-iac-tiles" />
              }
              tags={["NestJS", "TypeScript", "PostgreSQL", "Google Cloud", "Twilio"]}
              links={[
                { label: L.case, href: "/proyectos/iaclinical", icon: "arrow-right" },
                { label: L.private, muted: true, icon: "shield" },
              ]}
            />
          </Reveal>
          <Reveal className="rp-bento-cell rp-bento-lg">
            <ProjectCard
              size="lg"
              reverse
              {...P.roam}
              media={
                <div className="rp-gamecard">
                  <MockScreen variant="game" title="Roam Survival" src={roam.cover} alt="Roam Survival" tag="Placeholder · key art" />
                </div>
              }
              badges={
                roam.stores?.length > 0 && (
                  <>
                    {roam.stores.map((s) => (
                      <StoreBadge key={s} store={storeName(s)} icon={storeName(s) === "Steam" ? "play" : "phone"} href={s} />
                    ))}
                  </>
                )
              }
              tags={["Unreal Engine 4", "Blueprints", "Blender"]}
              links={linksOf(L, { ...roam, stores: [] })}
            />
          </Reveal>
          <Reveal className="rp-bento-cell rp-bento-md">
            <ProjectCard
              size="md"
              {...P.roperito}
              media={
                <BrowserFrame url={hostOf(roperito.site) || "roperitoweb.netlify.app"} className={roperito.windowCapture ? "is-window-capture" : undefined}>
                  <MockScreen variant="marketplace" src={roperito.cover} alt="Roperito, página principal" />
                </BrowserFrame>
              }
              tags={["React", "Node", "Express", "PostgreSQL", "Socket.io", "Firebase"]}
              links={linksOf(L, roperito)}
            />
          </Reveal>
          <Reveal className="rp-bento-cell rp-bento-md" delay={60}>
            <ProjectCard
              size="md"
              {...P.tenapp}
              media={phone("tennis", "Tenapp, pantalla de la app", tenapp.images?.[0])}
              tags={["React Native", "Expo", "Firebase", "MercadoPago"]}
              links={linksOf(L, tenapp)}
            />
          </Reveal>
          <Reveal className="rp-bento-cell rp-bento-md">
            <ProjectCard
              size="md"
              {...P.checkauto}
              media={phone("car", "CheckAuto, pantalla de la app", checkauto.images?.[0])}
              tags={["React Native", "Firebase", "Push"]}
              links={linksOf(L, checkauto)}
            />
          </Reveal>
          <Reveal className="rp-bento-cell rp-bento-md" delay={60}>
            <ProjectCard
              size="md"
              {...P.others}
              media={
                <div className="hp-others">
                  {bullrunfrenzy.cover
                    ? <div className="rp-gamecard"><MockScreen src={bullrunfrenzy.cover} alt="Bull Run Frenzy" /></div>
                    : <MediaPlaceholder label="BullRunFrenzy" hint="Captura 16:10" icon="gamepad" />}
                  {mammamia.cover
                    ? <div className="rp-gamecard"><MockScreen src={mammamia.cover} alt="Mamma Mía, página principal" /></div>
                    : <MediaPlaceholder label="Mamma Mía" hint="Captura 16:10" icon="browser" />}
                </div>
              }
              tags={["Videojuego", "E-commerce", "React"]}
              links={[
                ...linksOf(L, bullrunfrenzy).map((l) => ({ ...l, label: "BullRunFrenzy · " + l.label })),
                ...linksOf(L, mammamia).map((l) => ({ ...l, label: "Mamma Mía · " + l.label })),
              ]}
            />
          </Reveal>
        </BentoGrid>
      </div>
    </section>
  );
}

function Experience({ t, lang }) {
  return (
    <section className="rp-section" id="experiencia">
      <div className="rp-container">
        <Reveal><SectionHeader index="03" eyebrow={t.expEyebrow} title={t.expTitle} /></Reveal>
        <Reveal><Timeline items={EXPERIENCE[lang]} /></Reveal>
      </div>
    </section>
  );
}

function Stack({ t }) {
  return (
    <section className="rp-section" id="stack">
      <div className="rp-container">
        <Reveal><SectionHeader index="04" eyebrow={t.stackEyebrow} title={t.stackTitle} lead={t.stackLead} /></Reveal>
        <Reveal><StackGrid groups={STACK} /></Reveal>
      </div>
    </section>
  );
}

function Ways({ t }) {
  return (
    <section className="rp-section" id="formas">
      <div className="rp-container">
        <Reveal><SectionHeader index="05" eyebrow={t.wayEyebrow} title={t.wayTitle} /></Reveal>
        <div className="rp-principles">
          {t.ways.map((w, i) => (
            <Reveal key={w.title} delay={i * 60}>
              <PrincipleCard icon={w.icon} index={"0" + (i + 1)} title={w.title}>{w.text}</PrincipleCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact({ t }) {
  return (
    <section className="rp-section hp-contact" id="contacto">
      <div
        className="hp-contact-bg rp-aurora"
        aria-hidden="true"
        style={{ "--ax": "20%", "--ay": "40%", "--bx": "70%", "--by": "20%" }}
      />
      <div className="rp-container">
        <Reveal>
          <p className="rp-eyebrow" style={{ marginBottom: 20 }}>
            <span className="rp-eyebrow-idx">06</span>
            <span className="rp-eyebrow-rule" />
            {t.contactEyebrow}
          </p>
          <ContactPanel
            title={t.contactTitle}
            lead={t.contactLead}
            email={LINKS.email}
            cvLabel={t.cv}
            cvHref={LINKS.cv}
            copyLabel={t.copy}
            copiedLabel={t.copied}
            links={[
              { label: "LinkedIn", icon: "profile", href: LINKS.linkedin },
              { label: "GitHub", icon: "code", href: LINKS.github },
              { label: "WhatsApp", icon: "chat", href: LINKS.whatsapp },
            ]}
          />
        </Reveal>
        <div style={{ marginTop: 96 }}>
          <Footer text={t.footer} />
        </div>
      </div>
    </section>
  );
}

export function Home({ lang, onLang }) {
  const t = T[lang];
  const data = useProjects();

  // Al llegar desde otra ruta con /#seccion, la sección aún no existía cuando el navegador intentó saltar.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <div className="hp" lang={lang}>
      <NavBar links={t.nav} lang={lang} onLang={onLang} cvLabel={t.cv} cvHref={LINKS.cv} />
      <main>
        <Hero t={t} data={data} />
        <div className="rp-container hp-stats-wrap">
          <Reveal><StatBar items={t.stats} /></Reveal>
        </div>
        <About t={t} />
        <Projects t={t} data={data} />
        <Experience t={t} lang={lang} />
        <Stack t={t} />
        <Ways t={t} />
        <Contact t={t} />
      </main>
    </div>
  );
}
